// Firebase Auth singleton + dev-bypass for localhost ONLY.
//
// The firewall-safe replacement for the old client-side Clerk wiring
// (clerk-js was blocked by Ricoh's firewall). The browser now signs in
// against the Go backend's OPEN `POST /api/auth/signin` endpoint, which
// returns a Firebase custom token; the page exchanges it via
// `signInWithCustomToken` to get a real Firebase session. This module owns
// the Firebase SDK init + the same three-state machine clerk.ts had.
//
// Three mutually exclusive states, decided purely from whether the PUBLIC
// Firebase web config is present and the host the SPA is served from:
//
//   1. Real auth      — VITE_FIREBASE_API_KEY + VITE_FIREBASE_PROJECT_ID
//                        are present. Firebase is initialised lazily and
//                        the shell reads the Firebase auth state to gate.
//   2. Dev-bypass     — localhost AND no config. Firebase is never
//                        initialised, getFirebaseAuth() resolves null, and
//                        the rest of the app falls back to a fixed local
//                        identity (getToken() returns 'dev-token-local').
//   3. Misconfigured  — NOT localhost AND no config. This is a broken
//                        production deployment. We FAIL CLOSED:
//                        getFirebaseAuth() returns null but the shell treats
//                        this as a hard blocked error rather than silently
//                        bypassing.
//
// As in the old clerk.ts, the localhost guard makes dev-bypass impossible
// anywhere but localhost — so a production build shipped with empty
// VITE_FIREBASE_* env cannot turn into a wide-open auth-disable backdoor;
// it renders the friendly "blocked" state instead.
//
// The Firebase modular SDK is loaded via dynamic import so the anonymous
// `/share/:token` route and the initial app shell don't pay for it. Vite
// splits the import into its own chunk, fetched only when auth is needed.

import type { Auth } from 'firebase/auth';

const firebaseConfig = {
  apiKey: (import.meta.env.VITE_FIREBASE_API_KEY ?? '') as string,
  authDomain: (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '') as string,
  projectId: (import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '') as string,
  storageBucket: (import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '') as string,
  messagingSenderId: (import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '') as string,
  appId: (import.meta.env.VITE_FIREBASE_APP_ID ?? '') as string,
};

/** True only when the SPA is served from a local development host. */
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname === '::1');

/**
 * True when the PUBLIC Firebase web config is present. These values are NOT
 * secret (they ship in the client bundle); cloudbuild injects them at build
 * time, and they are simply absent during local development.
 */
export const hasFirebaseConfig = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

// Loud, build-time-visible warning when a production bundle ships without
// the Firebase config. We do NOT throw at import — throwing would blank the
// page with an opaque module error; instead the shell renders a friendly
// "blocked" state via isMisconfigured(), which is equally safe.
if (import.meta.env.PROD && !hasFirebaseConfig) {
  // eslint-disable-next-line no-console
  console.error(
    '[Firebase] No web config (VITE_FIREBASE_API_KEY + VITE_FIREBASE_PROJECT_ID must be set). ' +
      'This is a misconfigured production build — authentication is FAILING CLOSED and the app ' +
      'will render a blocked error. Inject the VITE_FIREBASE_* values and rebuild.',
  );
}

let authPromise: Promise<Auth | null> | null = null;

/**
 * Dev-bypass is active ONLY on localhost with no Firebase config. It can
 * never be true on a deployed (non-localhost) host, so a production build
 * with empty config cannot disable auth.
 */
export function isDevBypass(): boolean {
  return isLocalhost && !hasFirebaseConfig;
}

/**
 * The deployment is misconfigured when it is NOT localhost and has no
 * Firebase config. The shell must treat this as a hard blocked error (fail
 * closed) — never as a silent bypass.
 */
export function isMisconfigured(): boolean {
  return !isLocalhost && !hasFirebaseConfig;
}

/**
 * Returns the initialised Firebase Auth instance, or null when there is
 * nothing to initialise (dev-bypass, or — defensively — misconfigured: the
 * shell checks isMisconfigured() first and blocks before it ever trusts
 * this null).
 *
 * The Firebase SDK is fetched lazily via dynamic import so anonymous routes
 * never download it. Memoised so the in-flight init promise is shared by
 * every caller (shell gate + API client + login page).
 */
export function getFirebaseAuth(): Promise<Auth | null> {
  if (authPromise) return authPromise;
  // No config: nothing to initialise. Misconfigured callers are caught by
  // the shell's isMisconfigured() gate before this null is trusted; dev
  // -bypass callers expect null and fall back to the local identity.
  if (!hasFirebaseConfig) {
    authPromise = Promise.resolve(null);
    return authPromise;
  }
  authPromise = Promise.all([import('firebase/app'), import('firebase/auth')]).then(
    ([{ initializeApp }, { getAuth }]) => {
      const app = initializeApp(firebaseConfig);
      const auth = getAuth(app);
      // eslint-disable-next-line no-console
      console.info('[Firebase] Initialised — project:', firebaseConfig.projectId);
      return auth;
    },
  );
  return authPromise;
}

/**
 * Sign the user out of Firebase. No-op (resolves) when Firebase was never
 * initialised (dev-bypass / misconfigured). Safe to call unconditionally.
 */
export async function signOutFirebase(): Promise<void> {
  const auth = await getFirebaseAuth();
  if (!auth) return;
  const { signOut } = await import('firebase/auth');
  await signOut(auth);
}
